import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getAP, createAP, deleteAP, payAP } from '../../services/api';
import { FiPlus, FiTrash2, FiX, FiFileText, FiDollarSign } from 'react-icons/fi';
import Toast from '../../components/Toast';

const statusPill = { unpaid: 'pill-red', partial: 'pill-yellow', paid: 'pill-green' };

const AP = () => {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [payRecord, setPayRecord] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [form, setForm] = useState({ supplier: '', description: '', due_date: '', total_amount: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  const fetch = () => getAP({ status: statusFilter }).then(r => setList(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { fetch(); }, [statusFilter]);

  const handleAdd = async () => {
    setError('');
    if (!form.supplier || !form.total_amount) return setError('Supplier and total amount are required');
    setSaving(true);
    try {
      await createAP(form);
      setShowAdd(false);
      setForm({ supplier: '', description: '', due_date: '', total_amount: '' });
      showToast('Accounts payable created');
      fetch();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const handlePay = async () => {
    if (!payAmount || parseFloat(payAmount) <= 0) return;
    setSaving(true);
    try {
      await payAP(payRecord.id, { paid_amount: parseFloat(payAmount) });
      setPayRecord(null);
      setPayAmount('');
      showToast('Payment recorded');
      fetch();
    } catch (e) { showToast('Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete ${r.ap_number}?`)) return;
    await deleteAP(r.id);
    showToast('Deleted', 'error');
    fetch();
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('nav.ap')}</h1><p>{list.length}</p></div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}><FiPlus /> {t('form.newAP')}</button>
      </div>

      <div className="filters-row">
        <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">{t('common.all')}</option>
          <option value="unpaid">{t('subscriptions.due')}</option>
          <option value="partial">Partial</option>
          <option value="paid">{t('subscriptions.paid')}</option>
        </select>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr><th>#</th><th>AP {t('finance.crNumber').replace('CR', 'AP')}</th><th>{t('table.supplier')}</th><th>{t('table.description')}</th><th>{t('subscriptions.dueDate')}</th><th>{t('common.total')}</th><th>{t('subscriptions.paid')}</th><th>{t('dashboard.balance')}</th><th>{t('common.status')}</th><th>{t('common.actions')}</th></tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={10}><div className="empty-state"><div className="empty-icon"><FiFileText /></div><p>{t('finance.noRecords')}</p></div></td></tr>
            ) : list.map((r, i) => {
              const balance = parseFloat(r.total_amount) - parseFloat(r.paid_amount);
              const overdue = r.due_date && r.due_date < today && r.status !== 'paid';
              return (
                <tr key={r.id}>
                  <td data-label="#">{i + 1}</td>
                  <td data-label={`AP ${t('finance.crNumber').replace('CR', 'AP')}`}><strong>{r.ap_number}</strong></td>
                  <td data-label={t('table.supplier')}>{r.supplier}</td>
                  <td data-label={t('table.description')} style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description || '—'}</td>
                  <td data-label={t('subscriptions.dueDate')} style={{ color: overdue ? '#dc2626' : 'inherit', fontWeight: overdue ? 700 : 'normal' }}>{r.due_date || '—'}{overdue && <span className="pill pill-red" style={{ marginLeft: 4, fontSize: 10 }}>{t('followup.overdue')}</span>}</td>
                  <td data-label={t('common.total')}>{parseFloat(r.total_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td data-label={t('subscriptions.paid')} style={{ color: '#16a34a' }}>{parseFloat(r.paid_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td data-label={t('dashboard.balance')} style={{ color: balance > 0 ? '#dc2626' : '#16a34a', fontWeight: 700 }}>{balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td data-label={t('common.status')}><span className={`pill ${statusPill[r.status]}`}>{r.status}</span></td>
                  <td data-label={t('common.actions')}>
                    <div className="action-btns">
                      {r.status !== 'paid' && (
                        <button className="btn btn-sm btn-primary" onClick={() => { setPayRecord(r); setPayAmount(''); }}><FiDollarSign /> Pay</button>
                      )}
                      <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      {showAdd && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <div><h3>New Accounts Payable</h3></div>
              <button className="btn-icon" onClick={() => setShowAdd(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              {error && <div className="error-msg">{error}</div>}
              <div className="form-grid">
                <div className="form-group form-full"><label>Supplier <span className="required">*</span></label><input value={form.supplier} onChange={set('supplier')} /></div>
                <div className="form-group"><label>Total Amount <span className="required">*</span></label><input type="number" step="0.01" value={form.total_amount} onChange={set('total_amount')} /></div>
                <div className="form-group"><label>Due Date</label><input type="date" value={form.due_date} onChange={set('due_date')} /></div>
                <div className="form-group form-full"><label>Description</label><textarea value={form.description} onChange={set('description')} rows={2} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Pay Modal */}
      {payRecord && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <div><h3>Record Payment</h3><p>{payRecord.ap_number} — {payRecord.supplier}</p></div>
              <button className="btn-icon" onClick={() => setPayRecord(null)}><FiX /></button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                Remaining: <strong style={{ color: '#dc2626' }}>{(parseFloat(payRecord.total_amount) - parseFloat(payRecord.paid_amount)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
              </p>
              <div className="form-group">
                <label>Payment Amount <span className="required">*</span></label>
                <input type="number" step="0.01" value={payAmount} onChange={e => setPayAmount(e.target.value)} placeholder="0.00" autoFocus />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setPayRecord(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handlePay} disabled={saving}>{saving ? 'Saving...' : 'Record Payment'}</button>
            </div>
          </div>
        </div>
      )}
      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default AP;
